/**
 * Symbol Picker Modal Component
 * Modal for browsing and searching LaTeX symbols
 */

import React, { useState, useMemo, useEffect } from 'react'
import { EditorView } from '@codemirror/view'
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { getSelection } from '@/utils/latex/insertionHelpers'
import {
  getCategorizedSymbols,
  searchSymbols,
  getCategoryName,
  getCategoryIcon,
  getSymbolPriority,
  type SymbolCategory,
  type CategorizedSymbol
} from '@/utils/latex/symbolData'

interface SymbolPickerModalProps {
  view: EditorView | null
  isOpen: boolean
  onClose: () => void
  theme?: 'light' | 'dark'
}

// Store recent symbols in localStorage
const RECENT_SYMBOLS_KEY = 'latex_recent_symbols'
const MAX_RECENT_SYMBOLS = 20

function getRecentSymbols(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_SYMBOLS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function addRecentSymbol(command: string) {
  try {
    const recent = getRecentSymbols()
    const updated = [command, ...recent.filter(cmd => cmd !== command)].slice(0, MAX_RECENT_SYMBOLS)
    localStorage.setItem(RECENT_SYMBOLS_KEY, JSON.stringify(updated))
  } catch {
    // Ignore localStorage errors
  }
}

const SymbolPickerModal: React.FC<SymbolPickerModalProps> = ({ view, isOpen, onClose, theme = 'light' }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<SymbolCategory | 'recent' | 'all'>('recent')
  const [recentSymbols, setRecentSymbols] = useState<string[]>(getRecentSymbols())

  const categorizedSymbols = useMemo(() => getCategorizedSymbols(), [])
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return []
    return searchSymbols(searchQuery)
  }, [searchQuery])

  useEffect(() => {
    if (isOpen) {
      setRecentSymbols(getRecentSymbols())
      setSearchQuery('')
      setSelectedCategory('recent')
    }
  }, [isOpen])

  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape, true)
      return () => {
        document.removeEventListener('keydown', handleEscape, true)
      }
    }
  }, [isOpen, onClose])

  /**
   * Extract just the symbol character(s) from the detail field
   * Detail format is often like: "⊪ (\"amssymb\" command)" or "⦀ (\"fourier\" command)"
   * We want to extract just the symbol part before any parentheses
   */
  const extractSymbol = (detail: string | undefined): string => {
    if (!detail) return '?'
    // Extract everything before the first opening parenthesis
    const match = detail.match(/^([^(]+)/)
    if (match) {
      return match[1].trim()
    }
    return detail.trim()
  }

  const handleInsertSymbol = (symbol: CategorizedSymbol, e?: React.MouseEvent) => {
    if (!view) return
    
    // Prevent event from bubbling up
    if (e) {
      e.stopPropagation()
    }

    const selection = getSelection(view)
    const command = `\\${symbol.command}`
    const cursorPos = selection.from + command.length

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: command
      },
      selection: {
        anchor: cursorPos
      }
    })

    addRecentSymbol(symbol.command)
    setRecentSymbols(getRecentSymbols())
    view.focus()
    
    // Close the modal after inserting
    onClose()
  }

  const getDisplaySymbols = (): CategorizedSymbol[] => {
    if (searchQuery.trim()) {
      return searchResults
    }
    
    if (selectedCategory === 'recent') {
      const recent = recentSymbols
        .map(cmd => {
          // Find symbol by command name
          const allSymbols = Object.values(categorizedSymbols).flat()
          return allSymbols.find(s => s.command === cmd)
        })
        .filter((s): s is CategorizedSymbol => s !== undefined)
      return recent.length > 0 ? recent : []
    }
    
    if (selectedCategory === 'all') {
      return Object.values(categorizedSymbols).flat()
    }
    
    return categorizedSymbols[selectedCategory] || []
  }

  const displaySymbols = getDisplaySymbols()

  if (!isOpen) return null

  const dialogClass = clsx(
    'fixed inset-0 z-50 flex items-center justify-center',
    theme === 'dark' ? 'bg-black/50' : 'bg-black/30'
  )

  const contentClass = clsx(
    'relative rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col',
    theme === 'dark' ? 'bg-gray-800' : 'bg-white',
    'overflow-hidden' // Prevent content from overflowing and affecting layout
  )

  const inputClass = clsx(
    'w-full px-4 py-2 pl-10 rounded-md border text-sm',
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400'
      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
  )

  const categoryButtonClass = (isActive: boolean) => clsx(
    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
    isActive
      ? 'bg-primary-600 text-white'
      : theme === 'dark'
      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
  )

  const symbolButtonClass = clsx(
    'p-2 rounded-md transition-colors text-center',
    theme === 'dark'
      ? 'hover:bg-gray-700 text-gray-100'
      : 'hover:bg-gray-100 text-gray-900',
    'border border-transparent hover:border-gray-300 dark:hover:border-gray-600',
    'w-full h-full'
  )

  const categories: Array<SymbolCategory | 'recent' | 'all'> = [
    'recent',
    'greek',
    'operators',
    'arrows',
    'relations',
    'sets',
    'delimiters',
    'misc',
    'all'
  ]

  return (
    <div className={dialogClass} onClick={onClose}>
      <div className={contentClass} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={clsx('flex items-center justify-between p-4 border-b', theme === 'dark' ? 'border-gray-700' : 'border-gray-200')}>
          <h3 className={clsx('text-lg font-semibold', theme === 'dark' ? 'text-gray-100' : 'text-gray-900')}>
            Symbol Picker
          </h3>
          <button
            onClick={onClose}
            className={clsx(
              'p-1 rounded-md',
              theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
            )}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className={clsx(
              'absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4',
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            )} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search symbols by name or LaTeX command..."
              className={inputClass}
              autoFocus
            />
          </div>
        </div>

        {/* Category Tabs */}
        {!searchQuery.trim() && (
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto flex-shrink-0">
            <div className="flex gap-2 min-w-max">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedCategory(category)
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={categoryButtonClass(selectedCategory === category)}
                >
                  {category === 'recent' ? '⭐ Recent' : category === 'all' ? 'All' : getCategoryIcon(category as SymbolCategory) + ' ' + getCategoryName(category as SymbolCategory)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Symbols Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {displaySymbols.length === 0 ? (
            <div className={clsx('text-center py-12', theme === 'dark' ? 'text-gray-400' : 'text-gray-500')}>
              {searchQuery.trim() ? 'No symbols found' : 'No symbols in this category'}
            </div>
          ) : (
            <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2 auto-rows-fr">
              {displaySymbols.map((symbol) => {
                // Check if this is a priority/common symbol
                const isCommon = getSymbolPriority(symbol) > 0
                return (
                  <button
                    key={symbol.command}
                    onClick={(e) => handleInsertSymbol(symbol, e)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={clsx(
                      symbolButtonClass,
                      isCommon && 'ring-1 ring-primary-300 dark:ring-primary-600',
                      'flex items-center justify-center min-h-[60px]'
                    )}
                    title={`${symbol.command}${symbol.documentation ? `\n${symbol.documentation}` : ''}`}
                  >
                    <div className="text-2xl font-medium leading-tight">{extractSymbol(symbol.detail)}</div>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={clsx('px-4 py-2 border-t text-xs', theme === 'dark' ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500')}>
          Click a symbol to insert its LaTeX command. Press Escape to close.
        </div>
      </div>
    </div>
  )
}

export default SymbolPickerModal

